import { loadManagerFromZeroGuid as loadUserFromZeroManagerGuid, loadUserFromZeroGuid } from 'actions/user';
import { ArrayHelpers, Loading } from '@salesperformanceint/e4shared';
import { getPermissions } from 'helpers/permissions';
import { translateAppraisalJson } from 'helpers/translations';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { apiGet, apiPatch } from 'services';
import { ReviewState } from 'views/user/appraisals';
import Wizard from 'views/user/appraisals/components/wizard';
import posthog from 'posthog-js';

export const ZeroReview = ({ history, match }) => {
    const dispatch = useDispatch();
    const guid = match.params.guid;
    const { coaching, downline, user } = useSelector(state => state.user);
    const working = useSelector(state => state.user.working);
    const isManager = match.path.indexOf('zero-staff-review') >= 0;
    const isUser = match.path.indexOf('zero-review') >= 0;

    const [activeAppraisal, setActiveAppraisal] = React.useState(null);
    const [appraisals, setAppraisals] = React.useState(null);
    const [congrats, setCongrats] = React.useState(false);
    // const [error, setError] = React.useState(false);
    const [inReview, setInReview] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    // const [pastAppraisals, setPastAppraisals] = React.useState(null);
    // const [pastIndex, setPastIndex] = React.useState(0);
    const [predictions, setPredictions] = React.useState(null);
    const [permissions, setPermissions] = React.useState(getPermissions());
    const [profile, setProfile] = React.useState({});
    // const [showPast, setShowPast] = React.useState(false);
    // const [showReset, setShowReset] = React.useState(false);
    // const [showSchedule, setShowSchedule] = React.useState(set);
    const [stage, setStage] = React.useState(-1);
    const [state, setState] = React.useState({});

    const api = {
        login: guid => {
            if (isManager) {
                dispatch(loadUserFromZeroManagerGuid(guid, history));
            } else {
                dispatch(loadUserFromZeroGuid(guid, history));
            }
        },
        loadAppraisals: () => {
            apiGet(`appraisals/${profile.id}`, user.guid, response => {
                setAppraisals(response);
                setLoading(false);
            });
        },
        updateAppraisal: app => {
            const temp = { ...activeAppraisal };
            let predictions = {};

            if (isUser) {
                temp.user_Predictions = app.user_Predictions;
                temp.user_Competency_Comments = app.user_Competency_Comments;
                temp.user_Comments = app.user_Comments;
                temp.user_Comments_Done = app.user_Comments_Done;
                predictions = JSON.parse(app.user_Predictions || '{}');
            } else {
                temp.manager_Predictions = app.manager_Predictions;
                temp.manager_Competency_Comments = app.manager_Competency_Comments;
                temp.manager_Comments = app.manager_Comments;
                temp.manager_Comments_Done = app.manager_Comments_Done;
                predictions = JSON.parse(app.manager_Predictions || '{}');
            }

            temp.goals = app.goals;

            temp.competencies.forEach(x => {
                x.level = predictions[x.id]?.level;
            });

            // this updates the UI even if there is an error
            setActiveAppraisal(temp);

            posthog.capture(
                'review',
                {
                    type: 'comment',
                    isManager: isManager,
                    isUser: isUser,
                    reviewId: app.id,
                    manager_Id: app.manager_Id,
                    profile_Id: app.profile_Id,
                },
                { transport: 'sendBeacon' }
            );

            apiPatch(
                'appraisal-comment',
                app,
                user.guid,
                () => {
                    isManager && app.manager_Comments_Done && setCongrats(true);
                    isUser && app.user_Comments_Done && setCongrats(true);
                },
                result => {
                    posthog.capture(
                        'review',
                        {
                            type: 'comment-failed',
                            statusCode: result.response.status,
                            isManager: isManager,
                            isUser: isUser,
                            reviewId: app.id,
                            manager_Id: app.manager_Id,
                            profile_Id: app.profile_Id,
                        },
                        { transport: 'sendBeacon' }
                    );
                    if (result.response.status === 409) {
                        window.location.href = window.location.href; //reload as it should be completed so let the standard logic take over
                    }
                }
            );
        },
    };

    React.useEffect(() => {
        if (profile?.id && user?.id) {
            setPermissions(getPermissions(profile, user, coaching, downline));
        }
    }, [profile?.id, user?.id]);

    React.useEffect(() => {
        if (user) {
            if (isManager) {
                apiGet(`zero-profile-from-manager/${guid}`, user.guid, response => setProfile(response));
            } else {
                setProfile(user);
            }
        }
    }, [user, isManager]);

    React.useEffect(() => {
        if (guid) {
            api.login(guid);
        }
    }, [guid]);

    React.useEffect(() => {
        if (profile?.id) {
            api.loadAppraisals();
        }
    }, [profile]);

    // Set Active appraisal
    React.useEffect(() => {
        if (appraisals) {
            const active = appraisals.find(a => a.guid === guid || a.manager_Guid === guid);

            if (active.submission) {
                apiGet(`appraisal-json/${active.id}`, user.guid, json => {
                    const temp = { ...active };
                    temp.json = JSON.stringify(json);
                    temp.competencies = json.competencies || [];
                    translateAppraisalJson(user.language_Code, temp);
                    setActiveAppraisal(temp);
                });
            } else {
                setActiveAppraisal(active);
            }
        }
    }, [appraisals]);

    // Set If All Done?
    React.useEffect(() => {
        isUser && activeAppraisal?.user_Comments_Done && setCongrats(true);
        isManager && activeAppraisal?.manager_Comments_Done && setCongrats(true);
    }, [activeAppraisal]);

    // Set State
    React.useEffect(() => {
        if (user?.id && profile?.id) {
            const tempState = ReviewState(appraisals, activeAppraisal, user, profile, true);
            setState(tempState);
        }
    }, [activeAppraisal, profile?.id, user?.id]);

    // Set Predictions and Stage
    React.useEffect(() => {
        if (activeAppraisal && profile.id) {
            let rawPredictions = state.user ? activeAppraisal.user_Predictions : activeAppraisal.manager_Predictions;
            if (rawPredictions === '') rawPredictions = null;
            setPredictions(predictions ? JSON.parse(rawPredictions) : []);
            // if (showPast && pastAppraisals) {
            //     setStage(activeAppraisal.competencies.length);
            // } else if (activeAppraisal && state.completed && !state.finalStarted) {
            //     setStage(activeAppraisal.competencies.length);
            // } else if (activeAppraisal && state.finalStarted && !state.past) {
            //     setStage(activeAppraisal.competencies.length);
            // }
        }
    }, [activeAppraisal, profile.id, state]);

    return loading || working ? (
        <Loading />
    ) : (
        <Wizard
            appraisal={activeAppraisal}
            congrats={congrats}
            // finalise={finalise}
            // finalising={state.finalStarted && !state.past}
            inReview={inReview}
            permissions={permissions}
            profile={profile}
            // setAgreedRatingValue={setAgreedRatingValue}
            setCongrats={setCongrats}
            setInReview={setInReview}
            setStage={s => {
                setStage(s);
                // setCongrats(false);
            }}
            // showBothManagerAndStaffRatingInfo={
            //     (state.finalStarted || state.past) && !(zero && !activeAppraisal?.manager_Guid)
            // }
            stage={stage}
            state={state}
            updateAppraisal={api.updateAppraisal}
        />
    );
};

export default ZeroReview;
